import type { Ref } from "vue";
import type { PriceItem, PriceRepresentation } from "../types";

type MaybeRef<T> = Ref<T> | T;

export function usePriceResource () {
  const batcherStore = useBatcherStore();

  /**
   * Get the pricing data with the given ID for the given product
   * @param productId The product ID
   * @param id The price ID
   * @param lazy Whether to block navigation
   * @return The matching pricing data
   */
  function getByProductId (
    productId: MaybeRef<number>,
    id: MaybeRef<number>,
    lazy = false,
  ) {
    const bulkGetEndpoint = "/api/products/pricing/bulk/ids/get";
    return batcherStore.useFetch<PriceRepresentation>( bulkGetEndpoint, {
      id,
      productId,
    }, { lazy } );
  }

  /**
   * Get the pricing data with the given key for the given product
   * @param productKey The product key
   * @param id The price key
   * @param lazy Whether to block navigation
   * @return The matching pricing data
   */
  function getByProductKey (
    productKey: MaybeRef<string>,
    id: MaybeRef<number>,
    lazy = false,
  ) {
    const bulkGetEndpoint = "/api/products/pricing/bulk/keys/get";
    return batcherStore.useFetch<PriceRepresentation>( bulkGetEndpoint, {
      id,
      productKey,
    }, { queryKey: "keys", lazy } );
  }

  /**
   * Get all pricing data for the given product ID
   * @param productId The product ID
   * @param lazy Whether to block navigation
   * @return Any found pricing data
   */
  function listByProductId (
    productId: MaybeRef<number>,
    lazy = false,
  ) {
    const bulkListEndpoint = "/api/products/pricing/bulk/ids/list";
    return batcherStore.useFetch<PriceItem[]>( bulkListEndpoint, productId, {
      lazy,
    } );
  }

  /**
   * Get all pricing data for the given product key
   * @param productKey The product key
   * @param lazy Whether to block navigation
   * @return Any found pricing data
   */
  function listByProductKey (
    productKey: MaybeRef<string>,
    lazy = false,
  ) {
    const bulkListEndpoint = "/api/products/pricing/bulk/keys/list";
    return batcherStore.useFetch<PriceItem[]>( bulkListEndpoint, productKey, { queryKey: "keys", lazy } );
  }

  return {
    getByProductId,
    getByProductKey,
    listByProductId,
    listByProductKey,
  };
}
